import shortid from "shortid";
import { ModificationType, RecognitionRule } from "../Reference";
import { Period, Utils } from "revlock-webutils";
import RevenueArrangementUtils from "./RevenueArrangementUtils";
import * as AccountingUtils from "../AccountingUtils";
import { selectSSP } from "../SSPSelectorHelper";
import RevenueDeliveryDateHelper from "./RevenueDeliveryDateHelper";
import ItemType from "../reference/ItemType";
import { Codes } from "../ErrorCode";

/**
 * Processor class to manage the population of a Revenue Arrangement Item.
 * This class processes each Revenue Arrangement Item,
 * calculates SSP, delivery dates, list price, and validates the item.
 * @class RevenueArrangementItemProcessor
 * @constructor
 * @param {Object} salesOrder - The Sales Order object to process.
 * @param {Object} revenueArrangement - The Revenue Arrangement object containing the item.
 * @param {Object} revenueArrangementItem - The Revenue Arrangement Item to process.
 * @param {Object} context - The processing context containing client reference data and configurations.
 * @param {Object} arrangementContext - The context specific to the Revenue Arrangement processing.
 * @method process - Main method to orchestrate the processing of the Revenue Arrangement Item.
 * @returns {void}
 * @throws {Error} Throws an error if processing fails.
 * @example
 * const processor = new RevenueArrangementItemProcessor(salesOrder, revenueArrangement, revenueArrangementItem, context, arrangementContext);
 * processor.process();
 */
export default class RevenueArrangementItemProcessor {
    constructor(
        salesOrder,
        revenueArrangement,
        revenueArrangementItem,
        context,
        arrangementContext
    ) {
        this.salesOrder = salesOrder;
        this.revenueArrangement = revenueArrangement;
        this.item = revenueArrangementItem;
        this.context = context;
        this.arrangementContext = arrangementContext;
    }

    process() {
        const { salesOrder, revenueArrangement, item } = this;
        const { clientAttributes } = this.context;
        item.effectiveDate = revenueArrangement.effectiveDate;
        item.endDate = revenueArrangement.endDate;

        // Populate custom client attribute;
        populateClientAttributes(clientAttributes, item, salesOrder);

        // 1. Populate SSP
        this.populateSSP();

        // 2. Populate Delivery Dates
        this.populateDeliveryDates();

        // 3. Compute List Price
        this.computeListPrice();

        // 4. Populate Annual Extended Sale Price
        this.populateAnnualExtendedSalePrice();

        // 5. Validate Revenue Arrangement Item
        this.validate();
    }

    populateSSP() {
        const { salesOrder, item } = this;
        const { salesOrderItem } = this.item;
        const {
            clientReferenceData,
            clientAttributes,
            configProvider,
            setSalePriceForBundleComponents,
            disableCnAllocation
        } = this.context;

        // SSP is already loaded from snapshot.
        if (!item.ssp) {
            selectSSP(clientReferenceData, item, salesOrder);

            // Client has explicitly set standaone selling price of this item.
            // So so standalonePrice gets overridden.
            if (
                item.ssp &&
                (item.salesOrderItem.standaloneSellingPrice ||
                    item.salesOrderItem.isDeleted)
            ) {
                let override_ssp = Utils.copy(item.ssp);
                override_ssp.id = shortid.generate();
                override_ssp.standalonePrice =
                    item.salesOrderItem.isDeleted === true
                        ? 0
                        : item.salesOrderItem.standaloneSellingPrice;
                item.ssp = override_ssp;
            }

            if (!item.ssp && item.salesOrderItem.standaloneSellingPrice) {
                const override_ssp = Utils.copy(
                    defaultSSPForProductCode(shortid.generate())
                );
                override_ssp.attributes["productCode"] = "TERMINATE";

                if (item.salesOrderItem.product.recurring == true) {
                    override_ssp.recognitionRuleId = RecognitionRule.RATABLE;
                    delete override_ssp.recognizedOn;
                }

                override_ssp.standalonePrice =
                    item.salesOrderItem.standaloneSellingPrice;
                override_ssp.sspType = "Revenue";

                item.ssp = override_ssp;
            }

            if (
                !item.ssp &&
                setSalePriceForBundleComponents &&
                salesOrderItem.product.isBundle
            ) {
                item.ssp = createSSP(
                    configProvider,
                    salesOrder,
                    salesOrderItem,
                    item
                );
                if (salesOrderItem.product.entityType == "BASE") {
                    item.ssp.standalonePrice = 0;
                }
            }

            if (!item.ssp && configProvider.autoCreateSSP()) {
                item.ssp = createSSP(
                    configProvider,
                    salesOrder,
                    salesOrderItem,
                    item
                );
            }

            if (
                item.ssp &&
                salesOrder.contractId &&
                RevenueArrangementUtils.isCreditNoteItem(item.salesOrderItem) &&
                disableCnAllocation
            ) {
                let override_ssp = Utils.copy(item.ssp);
                override_ssp.standalonePrice = { type: "STANDALONE" };
                item.ssp = override_ssp;
            }
        }
    }

    populateDeliveryDates() {
        const { salesOrder, revenueArrangement, item } = this;
        const { salesOrderItem } = item;
        const { raItemsBySoId } = this.arrangementContext;
        let { errors, isApiCall, configProvider, useProductListPrice } =
            this.context;

        const soi =
            this.arrangementContext.soiById[
                salesOrderItem.rootId || salesOrderItem.id
            ];

        if (
            item.ssp &&
            // && (!item.deliveryStartDate || soi.startDate !== item.deliveryStartDate)
            (item.isActive == undefined || item.isActive == false)
        ) {
            RevenueDeliveryDateHelper.populateDeliveryDates(
                salesOrder,
                revenueArrangement,
                soi,
                item.ssp,
                item,
                this.context,
                this.arrangementContext
            );
        }
    }

    computeListPrice() {
        const { salesOrder, revenueArrangement, item } = this;
        const { endDateInclusive, useProductListPrice } = this.context;
        const soi =
            this.arrangementContext.soiById[
                item.salesOrderItem.rootId || item.salesOrderItem.id
            ];

        soi.listPrice = Utils.isNumber(soi.listPrice)
            ? soi.listPrice
            : RevenueArrangementUtils.computeTermAmount(
                  soi.product,
                  soi.startDate,
                  soi.endDate,
                  soi.product.listPriceAmount || soi.product.listPrice
              );

        // If the sales order has prospective modification, recompute the listPrice
        if (
            useProductListPrice &&
            soi.isListPriceProRated &&
            revenueArrangement.modificationType ==
                ModificationType.PROSPECTIVE &&
            !ItemType.isFrozen(item.itemType)
        ) {
            let startDate = item.deliveryStartDate;
            if (
                Period.isDateBetween(
                    revenueArrangement.modificationDate,
                    startDate,
                    item.deliveryEndDate,
                    true,
                    endDateInclusive
                )
            ) {
                startDate = revenueArrangement.modificationDate;
            }
            soi.listPrice = AccountingUtils.computeListPrice(
                soi.product,
                startDate,
                item.deliveryEndDate
            );
        }

        // When
        if (
            useProductListPrice &&
            item.ssp &&
            item.ssp.recognitionRuleId ===
                RecognitionRule.PROPORTIONAL_PERFORMANCE
        ) {
            soi.listPrice = soi.product.listPriceAmount || 0;
        }
        soi.extendedListPrice = soi.quantity * soi.listPrice;
        // Populate listPrice and extended listPrice
    }

    populateAnnualExtendedSalePrice() {
        const { salesOrderItem } = this.item;
        const { startDate, endDate } = salesOrderItem;

        let duration = 12;

        if (startDate && endDate) {
            duration = Utils.getMonthDiff(startDate, endDate, true);
        }

        if (duration == 0) {
            duration = 12;
        }

        salesOrderItem.annualExtendedSalePrice =
            salesOrderItem.extendedSalePrice * (12 / duration);
        salesOrderItem.duration = duration;
    }

    validate() {
        const { salesOrder, item } = this;
        const { salesOrderItem } = item;
        const { configProvider } = this.context;

        let { errors } = this.context;
        if (!item.ssp) {
            errors.add(Codes.TRAN_11, {
                salesOrder,
                salesOrderItem,
                revenueArrangementItem: item
            });
            this.arrangementContext.foundSSP = false;
        }

        const soi =
            this.arrangementContext.soiById[
                salesOrderItem.rootId || salesOrderItem.id
            ];
        if (item.ssp && !soi.isActive) {
            const customFieldsConfig = configProvider.customFieldsConfig();
            errors.add(Codes.TRAN_30, {
                salesOrder,
                salesOrderItem: soi,
                customFields: customFieldsConfig
            });
        }

        if (
            item.ssp &&
            item.ssp.recognitionRuleId !== RecognitionRule.POINT_IN_TIME
        ) {
            if (soi.endDate && soi.startDate) {
                let duration = Utils.getMonthDiff(
                    soi.startDate,
                    soi.endDate,
                    false
                );

                const maxContractYears =
                    configProvider.maxContractYearsConfig();

                if (duration > maxContractYears * 12) {
                    errors &&
                        errors.add(Codes.TRAN_15, {
                            salesOrder,
                            salesOrderItem,
                            maxContractYears
                        });
                } else if (duration < 0) {
                    errors &&
                        errors.add(Codes.TRAN_18, {
                            salesOrder,
                            salesOrderItem
                        });
                }
            }
        }
    }
}

const defaultSSPForProductCode = (id, attributes) => ({
    attributes: attributes || {},
    commissionRecognitionRuleId: 2,
    deliveryStartDate: "",
    recognitionRuleId: RecognitionRule.POINT_IN_TIME,
    id,
    standalonePrice: 0,
    recognizedOn: "startDate"
});

function populateClientAttributes(clientAttributes, item, salesOrder) {
    RevenueArrangementUtils.populateClientAttributes(
        clientAttributes,
        ["revenueArrangementItem"],
        {
            revenueArrangementItem: item,
            salesOrder
        }
    );
}

/**
 * Auto create SSP for the sales order item
 * @param configProvider
 * @param salesOrder
 * @param salesOrderItem
 * @param item
 * @returns
 */
function createSSP(configProvider, salesOrder, salesOrderItem, item) {
    let sspFields = configProvider.sspFieldConfig();
    const sspKeyBuilder = Utils.createCompositeKey(sspFields);

    const missingSSP = RevenueArrangementUtils.fetchBusinessKeyValues(
        salesOrder,
        salesOrderItem,
        sspFields
    );

    const ssp = Utils.copy(
        defaultSSPForProductCode(shortid.generate(), missingSSP)
    );
    ssp.id = Utils.searchValue(sspKeyBuilder(missingSSP));

    ssp.standalonePrice = { type: "AS_IS" };

    if (AccountingUtils.isDiscountProduct(item.salesOrderItem.product)) {
        ssp.recognitionRuleId = RecognitionRule.POINT_IN_TIME;
        ssp.standalonePrice = 0;
    } else {
        if (item.salesOrderItem.product.recurring == true) {
            ssp.recognitionRuleId = RecognitionRule.RATABLE;
            delete ssp.recognizedOn;
        }
    }

    const isMeteredEnabled = configProvider.isMeteredEnabled();

    if (
        isMeteredEnabled &&
        AccountingUtils.isMeteredProduct(item.salesOrderItem.product)
    ) {
        ssp.recognitionRuleId = RecognitionRule.PROPORTIONAL_PERFORMANCE;
    }

    ssp.sspType = "Revenue";

    // Save the auto created SSP to be saved later.
    if (!salesOrder.autoCreatedSSP) salesOrder.autoCreatedSSP = {};
    salesOrder.autoCreatedSSP[ssp.id] = ssp;

    return ssp;
}
